import { memo, useMemo } from 'react';
import {
  ChatPanelEmptyState,
  ChatPanelAgentView,
  ChatPanelConversationView,
} from '@aimsos/ui';
import { foldWorkflowRuns } from '../../../utils/foldWorkflowRuns.js';

function AssistantChatBody({
  showEmptyState,
  showConversationSkeleton,
  showConversationThread,
  showAgentView,
  draft,
  setDraft,
  processing,
  chatInputAttachmentProps,
  modelChatProps,
  chatInputDocuments,
  documentSearchProps,
  handleDocumentSelect,
  handleDocumentDelete,
  handleDocumentSearchMenuChange,
  handleEmptySend,
  handleConversationSend,
  handleAgentSend,
  effectiveAgentId,
  isCreatingConversation,
  conversationId,
  selectedConversationId,
  selectedAgentId,
  messages,
  isLoadingMessages,
  agentIsTyping,
  isStreaming,
  streamingStatusLabel = null,
  activityRunningLabel = 'Working…',
  activityToggleLabel = 'Toggle activity steps',
  formatActivitySummary = null,
  noticeNoReplyLabel = 'No reply was generated.',
  noticeFailedLabel = 'Something went wrong while generating the reply.',
  noticeRetryLabel = 'Retry',
  onRetryNotice = null,
  isFullscreen,
  threadSearchProps,
  handleOpenMessageAsDocument,
  documentCreatedMessageIds,
  documentCardMetaByMessageId,
  starters,
  selectedAgent,
  isLoadingAgentDetails,
  morePromptsLabel,
  fewerPromptsLabel,
  embeddedHeroTitle = null,
  embeddedHeroDescription = null,
  composerPlaceholder = 'Ask anything…',
  noSelectionLabel = 'Select an agent and a conversation to continue.',
  aboveComposerSlot = null,
}) {
  // Fold each workflow run's persisted milestone messages into its inline
  // activity accordion (marker: metadata.references ref_kind "workflow_run"),
  // so after a reload the thread shows one collapsible group per run followed
  // by the run's final message — identical to the live-streamed shape. Hooks
  // must run unconditionally, before the early returns below.
  const displayMessages = useMemo(() => foldWorkflowRuns(messages), [messages]);

  if (showEmptyState) {
    return (
      <ChatPanelEmptyState
        aboveComposerSlot={aboveComposerSlot}
        chatInputProps={{
          value: draft,
          onChange: setDraft,
          onSend: (v) => void handleEmptySend(v),
          placeholder: composerPlaceholder,
          disabled: !effectiveAgentId || isCreatingConversation,
          isProcessing: processing,
          ...chatInputAttachmentProps,
          documents: chatInputDocuments,
          documentSearchProps,
          onDocumentSelect: handleDocumentSelect,
          onDocumentSearchDeleteClick: handleDocumentDelete,
          onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
          ...modelChatProps,
        }}
      />
    );
  }

  if (showConversationSkeleton) {
    return (
      <ChatPanelConversationView
        messages={[]}
        isLoadingMessages
        alignMessagesStart={isFullscreen}
        conversationScrollKey={selectedConversationId || conversationId || ''}
        chatInputProps={{
          value: draft,
          onChange: setDraft,
          onSend: () => {},
          placeholder: composerPlaceholder,
          disabled: true,
          isProcessing: true,
          ...chatInputAttachmentProps,
          documents: chatInputDocuments,
          documentSearchProps,
          onDocumentSelect: handleDocumentSelect,
          onDocumentSearchDeleteClick: handleDocumentDelete,
          onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
          ...modelChatProps,
        }}
      />
    );
  }

  if (showConversationThread) {
    return (
      <ChatPanelConversationView
        aboveComposerSlot={aboveComposerSlot}
        messages={displayMessages}
        isLoadingMessages={false}
        agentIsTyping={agentIsTyping}
        isStreaming={isStreaming}
        streamingStatusLabel={streamingStatusLabel}
        activityRunningLabel={activityRunningLabel}
        activityToggleLabel={activityToggleLabel}
        formatActivitySummary={formatActivitySummary}
        noReplyLabel={noticeNoReplyLabel}
        failedLabel={noticeFailedLabel}
        retryLabel={noticeRetryLabel}
        onRetry={onRetryNotice}
        alignMessagesStart={isFullscreen}
        conversationScrollKey={selectedConversationId || conversationId || ''}
        messageSearch={threadSearchProps}
        onOpenMessageAsDocument={handleOpenMessageAsDocument}
        documentCreatedMessageIds={documentCreatedMessageIds}
        documentCardMetaByMessageId={documentCardMetaByMessageId}
        chatInputProps={{
          value: draft,
          onChange: setDraft,
          onSend: (v) => void handleConversationSend(v),
          placeholder: composerPlaceholder,
          disabled: !(conversationId || selectedConversationId) || !selectedAgentId,
          isProcessing: processing,
          ...chatInputAttachmentProps,
          documents: chatInputDocuments,
          documentSearchProps,
          onDocumentSelect: handleDocumentSelect,
          onDocumentSearchDeleteClick: handleDocumentDelete,
          onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
          ...modelChatProps,
        }}
      />
    );
  }

  if (showAgentView) {
    const agentName = embeddedHeroTitle || selectedAgent?.name || selectedAgentId || 'Assistant';
    const modelText = selectedAgent?.metadata?.model || selectedAgent?.model || '—';
    const description =
      embeddedHeroDescription ||
      selectedAgent?.description ||
      selectedAgent?.metadata?.description ||
      undefined;

    return (
      <ChatPanelAgentView
        aboveComposerSlot={aboveComposerSlot}
        agentName={agentName}
        modelLabel={modelText}
        description={description}
        isLoadingAgentDetails={isLoadingAgentDetails}
        isLoadingMessages={isLoadingMessages}
        starters={starters}
        onStarterClick={(q) => void handleAgentSend(q)}
        morePromptsLabel={morePromptsLabel}
        fewerPromptsLabel={fewerPromptsLabel}
        chatInputProps={{
          value: draft,
          onChange: setDraft,
          onSend: (v) => void handleAgentSend(v),
          placeholder: composerPlaceholder,
          disabled: !selectedAgentId || isCreatingConversation,
          isProcessing: processing,
          ...chatInputAttachmentProps,
          documents: chatInputDocuments,
          documentSearchProps,
          onDocumentSelect: handleDocumentSelect,
          onDocumentSearchDeleteClick: handleDocumentDelete,
          onDocumentSearchMenuChange: handleDocumentSearchMenuChange,
          ...modelChatProps,
        }}
      />
    );
  }

  return (
    <p className="p-4 text-center text-sm text-content-muted">
      {noSelectionLabel}
    </p>
  );
}

export default memo(AssistantChatBody);
